import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { useSites } from '../sites.js';
import { fetchExport, save } from './ExportButtons.js';
import { ScoreLine } from './charts.js';
import { GRADE_FLOOR } from './GradeRing.js';
import { useRecentlyFixed } from './Improvements.js';
import { blindSpot, useOverview } from './site.js';
import { Button, Notice, SelectField, UrgencyBadge } from './ui.js';

type Tool = 'chat' | 'code' | 'developer';
const TOOLS: readonly Tool[] = ['chat', 'code', 'developer'];

/**
 * The side panel of every site page (T-89, site.css .rail): progress, on the dashboard the next
 * steps, and the fix instructions for a developer or an AI agent. On phones it is one collapsed
 * panel above the page, built only when opened (audit §3).
 */
export function SiteRail({
  siteId,
  variant,
  dashboard,
  help = false,
  settings = false,
}: {
  siteId: string;
  variant: 'side' | 'fold';
  /** The dashboard shows it all; the other pages progress without its line, and the guide. */
  dashboard: boolean;
  /** Beside the order form, what to expect instead of the guide (site-help.html). */
  help?: boolean;
  /** Beside the settings only the progress (site-settings.html): nothing there to fix. */
  settings?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cards = (
    <>
      <Progress siteId={siteId} full={dashboard} />
      {dashboard ? <NextSteps siteId={siteId} /> : null}
      {help ? <Expect /> : settings ? null : <AiGuide siteId={siteId} />}
    </>
  );
  if (variant === 'side')
    return (
      <aside className="rail" aria-label={t('rail.label')}>
        {cards}
      </aside>
    );
  return (
    <details className="rail-fold" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>{t('rail.label')}</summary>
      {/* Built only when opened: no hidden copy of the hints next to the page itself. */}
      {open ? <div className="rail">{cards}</div> : null}
    </details>
  );
}

function Progress({ siteId, full }: { siteId: string; full: boolean }) {
  const { t } = useTranslation();
  const overview = useOverview(siteId);
  const site = useSites().data?.items.find((s) => s.id === siteId);
  const recent = useRecentlyFixed(siteId);
  const health = overview.data?.health;
  if (!health) return null;
  const trend = site?.healthTrend ?? [];
  const delta = trend.length >= 2 ? health.score - (trend[0]?.score ?? health.score) : null;
  const next = health.nextGrade?.grade ?? null;
  const missing = next ? Math.max(0, GRADE_FLOOR[next] - health.score) : null;
  const open = overview.data?.openFindings;
  const urgent = open ? open.critical + open.high : 0;
  const total = open ? open.critical + open.high + open.medium + open.low + open.info : 0;
  const fixed = recent.fixed;
  // The period only with the count: before the plan is known it is not settled either.
  const fixedTitle = fixed === null ? undefined : t('rail.fixedTitle', { count: recent.days });
  return (
    <section className="card">
      <h3>{t('rail.progress')}</h3>
      <div className="prog">
        <span className={`gr ${health.grade.toLowerCase()}`} aria-hidden="true">
          {health.grade}
        </span>
        <div>
          <div className="big">
            {health.score}
            <small>/ 100</small>
            <span className="sr-only"> ({t('rail.gradeSr', { grade: health.grade })})</span>
          </div>
          <p>
            {next && missing !== null
              ? t('rail.toNext', { count: missing, grade: next })
              : t('rail.top')}
          </p>
          {full && delta !== null && delta !== 0 ? (
            <p className={delta > 0 ? 'up' : 'down'}>
              <span aria-hidden="true">{delta > 0 ? '▲ ' : '▼ '}</span>
              {t(delta > 0 ? 'rail.deltaUp' : 'rail.deltaDown', { points: Math.abs(delta) })}
            </p>
          ) : null}
        </div>
      </div>
      {full && trend.length >= 2 ? (
        <ScoreLine
          points={trend.map((p) => p.score)}
          label={t('rail.trendSr', {
            from: trend[0]?.score ?? 0,
            to: trend[trend.length - 1]?.score ?? 0,
          })}
          floor={next ? { score: GRADE_FLOOR[next], grade: next } : null}
        />
      ) : null}
      {/* Term before value, as a list must be read; the number shows above (site.css). */}
      <dl className="cnt3">
        <div title={fixedTitle}>
          <dt>
            {t('rail.fixed')}
            {fixedTitle ? <span className="sr-only"> ({fixedTitle})</span> : null}
          </dt>
          <dd>{fixed ?? '–'}</dd>
        </div>
        <div>
          <dt>{t('rail.urgent')}</dt>
          <dd className={urgent > 0 ? 'bad' : undefined}>{urgent}</dd>
        </div>
        <div>
          <dt>{t('rail.open')}</dt>
          <dd>{total}</dd>
        </div>
      </dl>
    </section>
  );
}

/** What the panel says without problems to fix: down, hidden, or really nothing to do. */
const RAIL_EMPTY = {
  down: 'rail.next.down',
  bot_protection: 'rail.next.bot',
  auth_required: 'rail.next.auth',
  none: 'site.top.empty',
} as const;

function NextSteps({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const overview = useOverview(siteId);
  const top = overview.data?.topFindings ?? [];
  const more = overview.data?.topMore ?? 0;
  if (!overview.data) return null;
  return (
    <section className="card">
      <h3>{t('rail.nextSteps')}</h3>
      {top.length === 0 ? (
        // Nothing to fix is not nothing to do when the site is down or out of sight (SP-A2.66).
        <p className="calm">{t(RAIL_EMPTY[blindSpot(overview.data) ?? 'none'])}</p>
      ) : (
        <ol>
          {top.map((f, i) => (
            <li key={f.id}>
              <i aria-hidden="true">{i + 1}</i>
              <div>
                <Link to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}>
                  {f.title}
                </Link>
                <br />
                <UrgencyBadge urgency={f.urgency} />
              </div>
            </li>
          ))}
        </ol>
      )}
      {more > 0 || top.length > 0 ? (
        <Link className="more" to={`/sites/${siteId}/findings`}>
          {t('rail.allProblems')}
        </Link>
      ) : null}
    </section>
  );
}

/** What follows a request for help (site-help.html; docs/pricing.md §3). */
function Expect() {
  const { t } = useTranslation();
  return (
    <section className="card">
      <h3>{t('rail.expect.title')}</h3>
      <ul className="steps-list">
        {(['quote', 'decide', 'work', 'invoice'] as const).map((step) => (
          <li key={step}>{t(`rail.expect.${step}`)}</li>
        ))}
      </ul>
    </section>
  );
}

function AiGuide({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const download = useMutation({
    mutationFn: async () => {
      const { blob, name } = await fetchExport(siteId, 'md', toLanguage(i18n.language));
      save(blob, name);
    },
  });
  return (
    <section className="card ki">
      <h3>{t('rail.ai.title')}</h3>
      <p>{t('rail.ai.text')}</p>
      <div className="acts">
        <Button size="sm" onClick={() => setOpen(true)}>
          {t('rail.ai.handOver')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          busy={download.isPending}
          onClick={() => download.mutate()}
        >
          {t('rail.ai.download')}
        </Button>
      </div>
      {download.isError ? <p role="alert">{errorMessage(download.error, t)}</p> : null}
      <small>{t('rail.ai.safe')}</small>
      {/* SP-A2.161 (owner, 14.10): without guarantee, at the owner's responsibility. */}
      <small>{t('rail.ai.risk')}</small>
      <AgentDialog siteId={siteId} open={open} onClose={() => setOpen(false)} />
    </section>
  );
}

/** The task list with an opening that suits the tool, to copy or save (T-89, site.css .modal). */
function AgentDialog({
  siteId,
  open,
  onClose,
}: {
  siteId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const textId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const [tool, setTool] = useState<Tool>('chat');
  const [copied, setCopied] = useState(false);
  const [scope, setScope] = useState<'all' | 'urgent'>('all');
  const site = useSites().data?.items.find((s) => s.id === siteId);
  const url = site?.url ?? '';
  const f = site?.openFindings;
  const urgent = f ? f.critical + f.high : null;
  const total = f ? f.critical + f.high + f.medium + f.low + f.info : null;
  const language = toLanguage(i18n.language);
  const tasks = useQuery({
    queryKey: ['site', siteId, 'findings.md', language, scope],
    queryFn: async () => (await fetchExport(siteId, 'md', language, scope)).blob.text(),
    enabled: open,
  });
  const text = tasks.data ? `${t(`rail.ai.intro.${tool}`, { url })}\n\n${tasks.data}` : '';

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
      // showModal() focuses the first link, „Hilfe anfordern“ in the notice (SP-A2.164): Enter
      // there would leave the dialog unread. Start on the first choice instead.
      el.querySelector('select')?.focus();
    }
    if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
    if (!open) setCopied(false);
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      document.getElementById(textId)?.focus();
      (document.getElementById(textId) as HTMLTextAreaElement | null)?.select();
    }
  };
  const saveText = () =>
    save(
      new Blob([text], { type: 'text/markdown' }),
      `${t('rail.ai.fileName')}-${tool}${scope === 'urgent' ? '-urgent' : ''}.md`,
    );

  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId} onClose={onClose} className="agent-dialog">
      {open ? (
        <div className="box">
          <h2 id={titleId}>{t('rail.ai.handOver')}</h2>
          <p className="bodyM">{t('rail.ai.dialogText')}</p>
          <p className="risk-note">
            {/* SP-A2.164: „Hilfe anfordern“ in the text leads to the order form of the site; the
                rail shows what to expect there instead of the guide, and the dialog goes with it. */}
            <Trans
              i18nKey="rail.ai.notice"
              components={{ help: <Link to={`/sites/${siteId}/help`} /> }}
            />
          </p>
          <div className="pair">
            <SelectField
              label={t('rail.ai.toolLabel')}
              value={tool}
              onChange={(e) => {
                setTool(e.target.value as Tool);
                setCopied(false);
              }}
            >
              {TOOLS.map((k) => (
                <option key={k} value={k}>
                  {t(`rail.ai.tools.${k}`)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('rail.ai.scopeLabel')}
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as 'all' | 'urgent');
                setCopied(false);
              }}
            >
              <option value="all">
                {total !== null
                  ? t('rail.ai.scope.allCount', { count: total })
                  : t('rail.ai.scope.all')}
              </option>
              <option value="urgent">
                {urgent !== null
                  ? t('rail.ai.scope.urgentCount', { count: urgent })
                  : t('rail.ai.scope.urgent')}
              </option>
            </SelectField>
          </div>
          {tasks.isError ? (
            <Notice kind="error">{errorMessage(tasks.error, t)}</Notice>
          ) : (
            <div className="field">
              <label htmlFor={textId}>{t('rail.ai.instructions')}</label>
              <textarea
                id={textId}
                readOnly
                rows={10}
                value={tasks.isPending ? t('rail.ai.loading') : text}
              />
            </div>
          )}
          <div className="acts">
            <Button onClick={saveText} disabled={!text}>
              {t('rail.ai.saveFile')}
            </Button>
            <Button variant="secondary" onClick={() => void copy()} disabled={!text}>
              {t('rail.ai.copy')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('request.close')}
            </Button>
            <small>{t('rail.ai.safe')}</small>
          </div>
          {copied ? <p role="status">{t('rail.ai.copied')}</p> : null}
        </div>
      ) : null}
    </dialog>,
    document.body,
  );
}
